import { AionCta } from "@/components/site/AionCta";
import { OG_ABOUT } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { GraduationCap } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Raj Shamani Education: Daly College, Degree & Qualification";
const DESC =
  "Raj Shamani studied at Daly College, Indore. His full education history, qualification, why he skipped an MBA, and how he actually learned business.";
const URL = "https://rajshamani.life/raj-shamani-education";
const OG = OG_ABOUT;

const faq = [
  {
    q: "What is Raj Shamani's education qualification?",
    a: "Raj Shamani completed his schooling at Daly College in Indore, Madhya Pradesh, one of India's oldest boarding schools, and went on to college in Indore. His formal qualification is a graduate-level business education, but most of his practical training came from working inside the family manufacturing business, Shamani Industries, from the age of 16.",
  },
  {
    q: "Which school did Raj Shamani go to?",
    a: "He studied at Daly College, Indore — an institution known for educating children of business and royal families across central India.",
  },
  {
    q: "Which college did Raj Shamani attend?",
    a: "He attended college in Indore while already working in the family business. He has spoken publicly about treating college as a base rather than the main event, since his day-to-day learning was happening on the factory floor and in B2B sales meetings.",
  },
  {
    q: "Does Raj Shamani have an MBA?",
    a: "No. Raj Shamani does not hold an MBA. He has consistently said that his business education came from running procurement, manufacturing and distribution at Shamani Industries, and later from interviewing hundreds of founders and investors on the Figuring Out podcast.",
  },
  {
    q: "What does Raj Shamani say about formal education?",
    a: "He does not dismiss formal education, but he argues that it should be paired with real execution. His recurring point is that a degree teaches frameworks while a business teaches consequences, and that the second one compounds faster for anyone who wants to build.",
  },
  {
    q: "How did Raj Shamani learn business without a business degree?",
    a: "By apprenticing inside a working company. He joined Shamani Industries at 16, learned raw-material procurement, plant operations, pricing and B2B distribution, then layered on public speaking, content and investing. The podcast later became a second education — hundreds of long-form conversations with operators and investors.",
  },
];

const timeline = [
  ["Schooling", "Daly College, Indore", "Boarding-school education in Indore, Madhya Pradesh."],
  ["Graduation", "College in Indore", "Studied while simultaneously working in the family business."],
  ["Age 16", "Shamani Industries", "Started on the factory floor — procurement, manufacturing, B2B sales."],
  ["Age 21", "United Nations", "Spoke on youth entrepreneurship, an early public-speaking milestone."],
  ["Age 25+", "Figuring Out", "Long-form interviews with founders, investors and operators as ongoing learning."],
];

export const Route = createFileRoute("/raj-shamani-education")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani education and qualification", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          breadcrumb: { "@id": `${URL}#breadcrumb` },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            url: "https://rajshamani.life/about",
            alumniOf: { "@type": "EducationalOrganization", name: "Daly College, Indore" },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: EducationPage,
});

function EducationPage() {
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Education" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <GraduationCap size={14} /> School, college & qualification
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Raj Shamani <span className="text-gradient-gold">education</span> and qualification
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Raj Shamani was schooled at{" "}
            <strong className="text-foreground">Daly College, Indore</strong> and graduated from
            college in Indore — but the part of his education that actually shaped the businesses
            he runs happened outside a classroom, starting at age 16 on the floor of{" "}
            <Link to="/shamani-industries" className="text-gold underline underline-offset-4">
              Shamani Industries
            </Link>
            .
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Education at a glance</h2>
        <dl className="rounded-xl border border-border bg-card divide-y divide-border/60">
          {[
            ["School", "Daly College, Indore"],
            ["College", "Indore, Madhya Pradesh"],
            ["Highest qualification", "Graduate (business)"],
            ["MBA", "No"],
            ["Started working", "Age 16, Shamani Industries"],
            ["Primary training", "Family manufacturing business + 500+ founder interviews"],
          ].map(([k, v]) => (
            <div key={k} className="px-6 py-4 flex flex-wrap gap-2 justify-between">
              <dt className="text-sm uppercase tracking-wide text-gold">{k}</dt>
              <dd className="text-muted-foreground">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">
          The two-track education: classroom and factory
        </h2>
        <p className="text-muted-foreground max-w-3xl mb-6 leading-relaxed">
          Most searches for &ldquo;Raj Shamani qualification&rdquo; expect a single line — a degree
          and an institution. The accurate answer is that he ran two educations in parallel. One
          was conventional: Daly College in Indore, then a graduate degree while living at home.
          The other was an apprenticeship inside a functioning manufacturing company, where the
          feedback loop was a purchase order rather than a grade.
        </p>
        <p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
          That second track is why his public advice sounds different from most business
          commentators in India. He is not describing case studies he read; he is describing
          margin pressure, distributor relationships and cash cycles he handled before he was
          twenty.
        </p>
        <div className="rounded-xl border border-border bg-card divide-y divide-border/60">
          {timeline.map(([stage, where, what]) => (
            <div key={stage} className="px-6 py-5 grid sm:grid-cols-[110px_1fr] gap-3">
              <div className="text-gold font-display font-bold">{stage}</div>
              <div>
                <div className="font-semibold text-sm">{where}</div>
                <div className="text-sm text-muted-foreground leading-relaxed mt-1">{what}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 text-muted-foreground">
          For the full biography see the{" "}
          <Link to="/about" className="text-gold underline underline-offset-4">
            Raj Shamani wiki
          </Link>
          , or read about his{" "}
          <Link to="/raj-shamani-family" className="text-gold underline underline-offset-4">
            family background
          </Link>{" "}
          and{" "}
          <Link to="/raj-shamani-age" className="text-gold underline underline-offset-4">
            age and date of birth
          </Link>
          .
        </p>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/raj-shamani-education" />

      <RelatedPages pages={["aion", "about", "faq", "podcast", "book", "ventures"]} />
    </div>
  );
}
